"use client"

import Link from "next/link"
import { useEffect, useState } from "react"

import { ActionBadge } from "@/components/ui/action-badge"
import { CheckCircleIcon } from "@/components/ui/icons"
import { useTranslations } from "@/lib/intl/use-translations"

import {
  buildPricingCardData,
  fetchPackageCounts,
  type PackageCounts,
} from "./pricing-card-content"

/**
 * PricingBreakdown — the deterministic package/price card rendered in the chat
 * widget alongside Ella's prose (AHD-11297, the same hybrid pattern as
 * MarkerBreakdown). glue emits the authoritative package prices over the
 * `pricing_card` SSE event (`PricingCardWire`); the widget builds the full card
 * — localized names/inclusions (reusing the `pricing` namespace), the blood-test
 * + add-on rows from its own price feed, and the test-page links. So a price is
 * never model-generated, and product strings stay in marketing-site.
 *
 * Two layouts exist; `list` is the chosen in-widget shape. `cards` is kept for
 * the dev harness comparison.
 */

// ── Wire payload (what glue emits) ─────────────────────────────────────────
export interface PricingCardWirePackage {
  id: "core" | "advanced" | "pro"
  /** Canonical CHF integer from glue's `CanonicalPrices` — never model output. */
  priceChf: number
}
/** Which shape glue asked for. `overview` = the full segmented card (chip path /
 *  general ask), scans + blood + membership as first-class tabs (AHD-11637);
 *  `package` = a named MRI package subset; `addon` = add-on(s) only; `blood` =
 *  the standalone blood check-up as a hero; `membership` = the 3-tier membership
 *  card. A layout hint — the rows are still driven by packages/addons.
 *  Forward-compatible: an absent or unknown value renders the overview, so glue
 *  can ship the richer payload before or after the widget handles it. */
export type PricingCardVariant =
  | "overview"
  | "package"
  | "addon"
  | "blood"
  | "membership"
/** Which tab the overview opens on (AHD-11637). Context-driven by glue: a blood
 *  question opens Blood, a membership question Membership, a general ask Scans. */
export type PricingTab = "scans" | "blood" | "membership"
export interface PricingCardWire {
  country: string
  locale: string
  /** `plan` (a quiz visitor's membership plan) is drawn by the
   *  `@ahead-health/ella-widget` card itself before it calls a content
   *  provider, so `buildPricingCardData` never builds one. */
  variant?: PricingCardVariant | "plan"
  /** Overview only: the tab to open first. Defaults to `scans` when absent. */
  focus?: PricingTab
  packages: PricingCardWirePackage[]
  /** Selected add-on slugs glue named (widget prices them from its own feed). */
  addons?: string[]
}

// ── Render model (widget-built: glue prices + widget-owned presentation) ───
export interface PricingRow {
  id: string
  name: string
  /** Localized full price label (the package's CHF amount), formatted from the wire. */
  priceLabel: string
  /** CMS-derived biomarker count (from `/api/package-counts`); omitted until it
   *  resolves client-side, or when the CMS is unavailable. */
  biomarkerCount?: number
  /** True when this row is meant to carry a biomarker count (a blood-inclusive
   *  product). Drives a reserved placeholder line while the count is still
   *  loading, so its late arrival fills space that was already there instead of
   *  reflowing every row below it. */
  expectsCount?: boolean
  highlights: string[]
  /** Test/package landing page for "see full details". */
  href?: string
  /** Most-recommended package → visual emphasis + "Most popular" badge. */
  featured?: boolean
}

export interface PricingAddOn {
  name: string
  priceLabel: string
  href?: string
  /** One-line description — shown only in the add-on-focused variant's cards. */
  description?: string
}

/** One membership tier (AHD-11637). Amounts are widget-owned CHF literals gated
 *  to CH (membership tiers are not Stripe products); names/tests reuse the
 *  existing localized `_membership` frequency keys. */
export interface MembershipTier {
  id: string
  name: string
  /** Localized per-year price label (formatted amount + the per-year suffix). */
  priceLabel: string
  /** One-line "1 advanced blood test + 1 follow-up" descriptor. */
  tests: string
  featured?: boolean
}

export interface PricingCardData {
  country: string
  locale: string
  currency: string
  /** Which shape to render (drives the layout branch in `PricingBreakdown`). */
  variant: PricingCardVariant
  /** Which overview tab opens first (scans/blood/membership). */
  focus: PricingTab
  /** The MRI scan packages, as expandable rows (no longer includes blood). */
  packages: PricingRow[]
  /** The standalone blood check-up as its own first-class entity (AHD-11637) —
   *  the Blood tab on the overview and the `blood` focused hero. */
  blood?: PricingRow
  /** Membership tiers — the Membership tab and the `membership` focused card.
   *  CH-only (absent on EUR markets, so no CHF literal ever leaks). */
  membership?: MembershipTier[]
  /** "from <price>/year" anchor label for the overview's collapsed membership
   *  summary (cheapest tier, formatted). */
  membershipFromLabel?: string
  /** → /{country}/{locale}/membership. */
  membershipHref?: string
  /** Localized membership subtitle (focused `membership` card only). */
  membershipSubtitle?: string
  addOns?: PricingAddOn[]
  /** "from CHF X" label for the overview's folded add-ons link (the cheapest of
   *  the curated add-ons, formatted). Set only on the overview; absent when no
   *  add-on price resolves. */
  addOnsFromLabel?: string
  /** → /{country}/{locale}/pricing — "see all add-ons" (card shows a curated few). */
  addOnsHref?: string
  /** → /{country}/{locale}/pricing (compare-all footer link). */
  compareHref?: string
}

/** Chrome translator (`chat.pricingCard` namespace); the `choose` label carries
 *  an ICU `{name}` value. */
type ChromeT = (key: string, values?: Record<string, unknown>) => string

function HighlightRow({ label }: { label: string }) {
  return (
    <div className="type-body-sm flex items-start gap-2 text-neutral-700">
      <CheckCircleIcon size={16} className="mt-0.5 shrink-0" />
      <span>{label}</span>
    </div>
  )
}

/**
 * The biomarker sub-line under a package name. Counts arrive from
 * `/api/package-counts` a beat after the card renders (glue's prices paint
 * instantly; the CMS count follows), so this holds the line's height with a
 * skeleton while the fetch is in flight for a count-bearing row. The count then
 * swaps into the same box — no row below it moves. A row that never expects a
 * count, or a resolved fetch that returned nothing (CMS down), renders nothing.
 */
function BiomarkerLine({
  count,
  expectsCount,
  pending,
  t,
}: {
  count?: number
  expectsCount?: boolean
  pending: boolean
  t: ChromeT
}) {
  if (count != null) {
    return (
      <span className="type-body-sm mt-0.5 block text-neutral-500">
        {t("biomarkers", { count })}
      </span>
    )
  }
  if (pending && expectsCount) {
    // Same `type-body-sm mt-0.5 block` box as the real line, so the skeleton →
    // text swap costs zero layout shift; the inline-block bar establishes the
    // line box height.
    return (
      <span className="type-body-sm mt-0.5 block" aria-hidden>
        <span className="inline-block h-3 w-24 animate-pulse rounded bg-neutral-100" />
      </span>
    )
  }
  return null
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={`text-neutral-400 transition-transform duration-200 ${
        open ? "rotate-180" : ""
      }`}
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ── Shared: one expandable package/entity row ─────────────────────────── */

/** A collapsed name + price row that expands to its inclusions + a details link.
 *  Shared by the compact list (package subset) and the overview's Scans tab. */
function PackageRow({
  p,
  t,
  isOpen,
  onToggle,
  countsPending,
}: {
  p: PricingRow
  t: ChromeT
  isOpen: boolean
  onToggle: () => void
  countsPending: boolean
}) {
  return (
    <div className="border-t border-neutral-100 first:border-t-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={`flex w-full items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-neutral-50 ${
          p.featured ? "bg-aubergine-50/60" : ""
        }`}
      >
        <span className="min-w-0">
          <span className="flex items-center gap-2">
            <span className="type-body-md font-medium text-neutral-900">
              {p.name}
            </span>
            {p.featured && (
              <ActionBadge label={t("mostPopular")} color="aubergine" />
            )}
          </span>
          <BiomarkerLine
            count={p.biomarkerCount}
            expectsCount={p.expectsCount}
            pending={countsPending}
            t={t}
          />
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <span className="type-body-md font-medium text-neutral-900">
            {p.priceLabel}
          </span>
          <ChevronDown open={isOpen} />
        </span>
      </button>

      {isOpen && (p.highlights.length > 0 || p.href) && (
        <div className="flex flex-col gap-1.5 px-4 pb-3.5">
          {p.highlights.map((h) => (
            <HighlightRow key={h} label={h} />
          ))}
          {p.href && (
            <Link
              href={p.href}
              className="type-body-sm text-deep-blue-800 mt-1 font-medium hover:underline"
            >
              {t("seeDetails")} →
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Layout: compact expandable list (package subset) ──────────────────── */

function PricingList({
  data,
  t,
  countsPending,
}: {
  data: PricingCardData
  t: ChromeT
  countsPending: boolean
}) {
  // Start every row collapsed — the card is a compact scan of names + prices,
  // and the customer taps the one they care about. Auto-expanding the featured
  // package made the card noticeably taller for no question that was asked.
  const [open, setOpen] = useState<string | null>(null)

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 px-4 py-2.5">
        <span className="type-body-sm font-medium text-neutral-900">
          {t("title")}
        </span>
      </div>

      {data.packages.map((p) => (
        <PackageRow
          key={p.id}
          p={p}
          t={t}
          isOpen={open === p.id}
          onToggle={() => setOpen(open === p.id ? null : p.id)}
          countsPending={countsPending}
        />
      ))}

      {/* Overview folds its curated add-ons into a single link so the card stays
          short; a focused turn (a package subset that carries the add-ons the
          customer named) still lists them as rows. */}
      {data.variant === "overview"
        ? data.addOnsFromLabel &&
          data.addOnsHref && (
            <Link
              href={data.addOnsHref}
              className="type-body-sm text-deep-blue-800 flex items-center gap-2 border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
            >
              {t("addOnsFrom", { price: data.addOnsFromLabel })} →
            </Link>
          )
        : data.addOns &&
          data.addOns.length > 0 && (
            <div className="border-t border-neutral-100 px-4 pt-3 pb-1">
              <span className="type-label-sm font-medium text-neutral-500 uppercase">
                {t("addOns")}
              </span>
              <div className="mt-1.5 flex flex-col">
                {data.addOns.map((a) => (
                  <div
                    key={a.name}
                    className="type-body-sm flex items-center gap-2 py-1.5 text-neutral-700"
                  >
                    {a.href ? (
                      <Link
                        href={a.href}
                        className="text-foreground hover:text-deep-blue-800 min-w-0 hover:underline"
                      >
                        {a.name}
                      </Link>
                    ) : (
                      <span className="min-w-0">{a.name}</span>
                    )}
                    <span className="ml-auto shrink-0 font-medium text-neutral-900">
                      {a.priceLabel}
                    </span>
                  </div>
                ))}
              </div>
              {data.addOnsHref && (
                <Link
                  href={data.addOnsHref}
                  className="type-body-sm text-deep-blue-800 mt-1 mb-1 inline-block font-medium hover:underline"
                >
                  {t("seeAllAddOns")} →
                </Link>
              )}
            </div>
          )}

      {data.compareHref && (
        <Link
          href={data.compareHref}
          className="type-body-sm text-deep-blue-800 block border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
        >
          {t("compareAll")} →
        </Link>
      )}
    </div>
  )
}

/* ── Layout: single-package hero (focused `package`, one selection) ─────── */

/**
 * The richer treatment for a one-package answer (AHD-11297 part B). No header
 * chrome, no chevron: the name and price carry weight and every inclusion is
 * visible, so a "what does Advanced cost?" reads as a considered answer rather
 * than a stray row of the overview card. Reuses the same reserved-height
 * biomarker skeleton so the late CMS count fills space that was already there.
 */
function PricingHero({
  pkg,
  compareHref,
  t,
  countsPending,
}: {
  pkg: PricingRow
  compareHref?: string
  t: ChromeT
  countsPending: boolean
}) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="type-title-sm font-medium text-neutral-900">
              {pkg.name}
            </span>
            {pkg.featured && (
              <ActionBadge label={t("mostPopular")} color="aubergine" />
            )}
          </div>
          <BiomarkerLine
            count={pkg.biomarkerCount}
            expectsCount={pkg.expectsCount}
            pending={countsPending}
            t={t}
          />
        </div>
        <span className="type-title-md ml-auto shrink-0 font-medium text-neutral-900">
          {pkg.priceLabel}
        </span>
      </div>

      {pkg.highlights.length > 0 && (
        <div className="mt-4 flex flex-col gap-2 border-t border-neutral-100 pt-4">
          {pkg.highlights.map((h) => (
            <HighlightRow key={h} label={h} />
          ))}
        </div>
      )}

      {pkg.href && (
        <div className="mt-4">
          <Link
            href={pkg.href}
            className="type-body-sm text-deep-blue-800 inline-flex items-center rounded-full border border-neutral-200 px-4 py-2 font-medium hover:bg-neutral-50"
          >
            {t("seeDetails")} →
          </Link>
        </div>
      )}

      {compareHref && (
        <Link
          href={compareHref}
          className="type-body-sm text-deep-blue-800 mt-3 inline-block font-medium hover:underline"
        >
          {t("compareAll")} →
        </Link>
      )}
    </div>
  )
}

/* ── Layout: add-on focused mini-cards (`addon` variant) ────────────────── */

/**
 * The `addon` variant — "how much is a DEXA scan?". No package rows, just the
 * named add-on(s) as compact cards: name, price, the one-line description the
 * pricing catalog already ships (fully localized), and a details link. One or
 * several stack cleanly.
 */
function PricingAddOnFocus({ data, t }: { data: PricingCardData; t: ChromeT }) {
  const addOns = data.addOns ?? []
  return (
    <div className="flex flex-col gap-2.5">
      {addOns.map((a) => (
        <div
          key={a.name}
          className="rounded-2xl border border-neutral-200 bg-white p-4"
        >
          <div className="flex items-baseline gap-3">
            <span className="type-body-lg font-medium text-neutral-900">
              {a.name}
            </span>
            <span className="type-body-lg ml-auto shrink-0 font-medium text-neutral-900">
              {a.priceLabel}
            </span>
          </div>
          {a.description && (
            <p className="type-body-sm mt-1 text-neutral-500">
              {a.description}
            </p>
          )}
          {a.href && (
            <Link
              href={a.href}
              className="type-body-sm text-deep-blue-800 mt-2 inline-block font-medium hover:underline"
            >
              {t("seeDetails")} →
            </Link>
          )}
        </div>
      ))}

      {data.addOnsHref && (
        <Link
          href={data.addOnsHref}
          className="type-body-sm text-deep-blue-800 px-1 py-1 font-medium hover:underline"
        >
          {t("seeAllAddOns")} →
        </Link>
      )}
    </div>
  )
}

/* ── Shared: membership tier rows ──────────────────────────────────────── */

function MembershipTierRows({
  tiers,
  t,
}: {
  tiers: MembershipTier[]
  t: ChromeT
}) {
  return (
    <>
      {tiers.map((tr) => (
        <div
          key={tr.id}
          className={`border-t border-neutral-100 px-4 py-3 first:border-t-0 ${
            tr.featured ? "bg-aubergine-50/60" : ""
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="type-body-md font-medium text-neutral-900">
              {tr.name}
            </span>
            {tr.featured && (
              <ActionBadge label={t("mostPopular")} color="aubergine" />
            )}
            <span className="type-body-md ml-auto shrink-0 font-medium text-neutral-900">
              {tr.priceLabel}
            </span>
          </div>
          <span className="type-body-sm mt-0.5 block text-neutral-500">
            {tr.tests}
          </span>
        </div>
      ))}
    </>
  )
}

/* ── Layout: membership 3-tier card (`membership` variant) ──────────────── */

/**
 * The focused membership answer (AHD-11637) — its own design, distinct from the
 * MRI package hero. A header + subtitle, then the three tiers (1x/2x/4x per year)
 * with their per-year price and what each includes, then a link to the
 * membership page. Amounts are widget-owned and CH-gated (built only for CHF).
 */
function MembershipCard({
  data,
  t,
  tp,
}: {
  data: PricingCardData
  t: ChromeT
  tp: (key: string) => string
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 px-4 py-3">
        <span className="type-title-sm font-medium text-neutral-900">
          {tp("tabMembershipLabel")}
        </span>
        {data.membershipSubtitle && (
          <span className="type-body-sm mt-0.5 line-clamp-2 block text-neutral-500">
            {data.membershipSubtitle}
          </span>
        )}
      </div>
      <MembershipTierRows tiers={data.membership ?? []} t={t} />
      {data.membershipHref && (
        <Link
          href={data.membershipHref}
          className="type-body-sm text-deep-blue-800 block border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
        >
          {t("seeMembership")} →
        </Link>
      )}
    </div>
  )
}

/* ── Layout: segmented tabs overview (`overview` variant, AHD-11637) ─────── */

/**
 * The overview card — scans, blood, and membership as first-class tabs. One panel
 * shows at a time, so all three products are visible in the tab bar without the
 * card growing past the AHD-11297 height budget. The initial tab is `data.focus`
 * (context-driven by glue: a blood question opens Blood, a membership question
 * Membership, a general ask Scans). A tab with no data (e.g. membership on a EUR
 * market) is dropped, never shown empty.
 */
function PricingTabs({
  data,
  t,
  tp,
  countsPending,
}: {
  data: PricingCardData
  t: ChromeT
  tp: (key: string) => string
  countsPending: boolean
}) {
  const tabs: { id: PricingTab; label: string }[] = []
  if (data.packages.length > 0)
    tabs.push({ id: "scans", label: tp("tabMriLabelShort") })
  if (data.blood) tabs.push({ id: "blood", label: tp("tabBloodLabelShort") })
  if (data.membership && data.membership.length > 0)
    tabs.push({ id: "membership", label: tp("tabMembershipLabelShort") })

  const initial = tabs.some((x) => x.id === data.focus)
    ? data.focus
    : (tabs[0]?.id ?? "scans")
  const [tab, setTab] = useState<PricingTab>(initial)
  const [openPkg, setOpenPkg] = useState<string | null>(null)

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      {tabs.length > 1 && (
        <div className="flex gap-1 border-b border-neutral-100 p-1.5">
          {tabs.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => setTab(x.id)}
              aria-pressed={tab === x.id}
              // no-raw-ids: `tab === x.id` is a boolean test selecting Tailwind classes — the id styles the tab, it is never rendered. Visible text is {x.label}.
              className={`type-body-sm flex min-h-[40px] flex-1 items-center justify-center rounded-lg px-2 py-2 font-medium transition-colors ${
                tab === x.id
                  ? "bg-deep-blue-950 text-white"
                  : "text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>
      )}

      {/* Scans tab: the MRI packages + the folded add-ons link. */}
      {tab === "scans" && (
        <>
          {data.packages.map((p) => (
            <PackageRow
              key={p.id}
              p={p}
              t={t}
              isOpen={openPkg === p.id}
              onToggle={() => setOpenPkg(openPkg === p.id ? null : p.id)}
              countsPending={countsPending}
            />
          ))}
          {data.addOnsFromLabel && data.addOnsHref && (
            <Link
              href={data.addOnsHref}
              className="type-body-sm text-deep-blue-800 flex items-center gap-2 border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
            >
              {t("addOnsFrom", { price: data.addOnsFromLabel })} →
            </Link>
          )}
          {/* "Compare packages" belongs only under Scans — it links to the MRI
              package comparison, so showing it under Blood/Membership would send
              the customer to the wrong page (AHD-11637 review). */}
          {data.compareHref && (
            <Link
              href={data.compareHref}
              className="type-body-sm text-deep-blue-800 block border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
            >
              {t("compareAll")} →
            </Link>
          )}
        </>
      )}

      {/* Blood tab: the standalone check-up, its count, inclusions + details. */}
      {tab === "blood" && data.blood && (
        <div className="px-4 py-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0">
              <span className="type-body-lg font-medium text-neutral-900">
                {data.blood.name}
              </span>
              <BiomarkerLine
                count={data.blood.biomarkerCount}
                expectsCount={data.blood.expectsCount}
                pending={countsPending}
                t={t}
              />
            </div>
            <span className="type-body-lg ml-auto shrink-0 font-medium text-neutral-900">
              {data.blood.priceLabel}
            </span>
          </div>
          {data.blood.highlights.length > 0 && (
            <div className="mt-3 flex flex-col gap-1.5">
              {data.blood.highlights.map((h) => (
                <HighlightRow key={h} label={h} />
              ))}
            </div>
          )}
          {data.blood.href && (
            <Link
              href={data.blood.href}
              className="type-body-sm text-deep-blue-800 mt-3 inline-block font-medium hover:underline"
            >
              {t("seeDetails")} →
            </Link>
          )}
        </div>
      )}

      {/* Membership tab: the three tiers + a link to the membership page. */}
      {tab === "membership" && data.membership && (
        <>
          <MembershipTierRows tiers={data.membership} t={t} />
          {data.membershipHref && (
            <Link
              href={data.membershipHref}
              className="type-body-sm text-deep-blue-800 flex items-center gap-2 border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
            >
              {t("seeMembership")} →
            </Link>
          )}
        </>
      )}
    </div>
  )
}

export function PricingBreakdown({ wire }: { wire: PricingCardWire }) {
  const tPricing = useTranslations("pricing")
  const tMembership = useTranslations("_membership")
  const tChrome = useTranslations("chat.pricingCard")
  // Biomarker counts fill in from the same-origin cached route after mount (the
  // card renders instantly from glue's prices; the count pops in a beat later,
  // like live prices do). {} on the server / on failure → no count shown.
  // `resolved` flips once the fetch settles (either way) so a count-bearing row
  // can reserve the line's height while pending — the count then swaps into that
  // reserved box instead of pushing every row below it down.
  const [counts, setCounts] = useState<PackageCounts>({})
  const [resolved, setResolved] = useState(false)
  useEffect(() => {
    let active = true
    fetchPackageCounts(wire.country).then((c) => {
      if (!active) return
      setCounts(c)
      setResolved(true)
    })
    return () => {
      active = false
    }
  }, [wire.country])
  // next-intl types t() to its known keys; our package/inclusion keys are
  // resolved dynamically (MarkerBreakdown widens `whatWeDetect` the same way).
  const tp = tPricing as unknown as (key: string) => string
  const data = buildPricingCardData(
    wire,
    tp,
    counts,
    tMembership as unknown as (key: string) => string
  )
  const t = tChrome as unknown as (
    key: string,
    values?: Record<string, unknown>
  ) => string
  // Add-on-only turn → focused mini-cards.
  if (data.variant === "addon") {
    return <PricingAddOnFocus data={data} t={t} />
  }
  // Membership-only turn → the 3-tier membership card.
  if (data.variant === "membership") {
    return <MembershipCard data={data} t={t} tp={tp} />
  }
  // Blood-only turn → the standalone check-up as a hero (same treatment as a
  // single package).
  if (data.variant === "blood" && data.blood) {
    return (
      <PricingHero
        pkg={data.blood}
        compareHref={data.compareHref}
        t={t}
        countsPending={!resolved}
      />
    )
  }
  // The overview → segmented tabs (scans | blood | membership).
  if (data.variant === "overview") {
    return <PricingTabs data={data} t={t} tp={tp} countsPending={!resolved} />
  }
  // A single named package with no add-ons gets the hero treatment; a subset of
  // packages (or a package + add-ons) stays in the compact expandable list.
  const heroPackage =
    data.variant === "package" &&
    data.packages.length === 1 &&
    (data.addOns?.length ?? 0) === 0
      ? data.packages[0]
      : null
  if (heroPackage) {
    return (
      <PricingHero
        pkg={heroPackage}
        compareHref={data.compareHref}
        t={t}
        countsPending={!resolved}
      />
    )
  }
  return <PricingList data={data} t={t} countsPending={!resolved} />
}
